import {create} from 'zustand';
import {inventoryApi} from '../services/api/inventoryApi';

export const useInventoryCatalogStore = create((set) => ({
  materials: [],
  readymades: [],
  purchaseOrders: [],
  isLoading: false,

  // Materials
  fetchMaterials: async (search, orgUnitId) => {
    set({isLoading: true});
    try {
      const response = await inventoryApi.materials.list(search, orgUnitId);
      set({materials: response.data.data});
    } finally {
      set({isLoading: false});
    }
  },
  createMaterial: async (data) => {
    const response = await inventoryApi.materials.create(data);
    const newItem = response.data.data;
    set(state => ({materials: [...state.materials, newItem]}));
    return newItem;
  },
  updateMaterial: async (id, data) => {
    const response = await inventoryApi.materials.update(id, data);
    const updated = response.data.data;
    set(state => ({
      materials: state.materials.map(a => (a.id === id ? updated : a)),
    }));
    return updated;
  },
  deleteMaterial: async (id) => {
    await inventoryApi.materials.remove(id);
    set(state => ({
      materials: state.materials.filter(a => a.id !== id),
    }));
  },
  uploadMaterialImages: async (id, mainImageFile, additionalSlots) => {
    const formData = new FormData();
    if (mainImageFile) {
      formData.append('mainImage', {
        uri: mainImageFile.uri,
        type: mainImageFile.type || 'image/jpeg',
        name: mainImageFile.fileName || 'main.jpg',
      });
    }
    const keepUrls = [];
    if (additionalSlots) {
      additionalSlots.forEach(slot => {
        if (slot.file) {
          formData.append('additionalImages', {
            uri: slot.file.uri,
            type: slot.file.type || 'image/jpeg',
            name: slot.file.fileName || 'additional.jpg',
          });
        } else if (slot.uri) {
          keepUrls.push(slot.uri);
        }
      });
    }
    if (keepUrls.length > 0) {
      formData.append('keepAdditionalUrls', keepUrls.join(','));
    }
    const response = await inventoryApi.materials.uploadImages(id, formData);
    const updated = response.data.data;
    set(state => ({
      materials: state.materials.map(a => (a.id === id ? updated : a)),
    }));
    return updated;
  },

  // Readymades
  fetchReadymades: async (search, orgUnitId) => {
    set({isLoading: true});
    try {
      const response = await inventoryApi.readymades.list(search, orgUnitId);
      set({readymades: response.data.data});
    } finally {
      set({isLoading: false});
    }
  },
  createReadymade: async (data) => {
    const response = await inventoryApi.readymades.create(data);
    const newItem = response.data.data;
    set(state => ({readymades: [...state.readymades, newItem]}));
    return newItem;
  },
  updateReadymade: async (id, data) => {
    const response = await inventoryApi.readymades.update(id, data);
    const updated = response.data.data;
    set(state => ({
      readymades: state.readymades.map(a => (a.id === id ? updated : a)),
    }));
    return updated;
  },
  deleteReadymade: async (id) => {
    await inventoryApi.readymades.remove(id);
    set(state => ({
      readymades: state.readymades.filter(a => a.id !== id),
    }));
  },
  uploadReadymadeImages: async (id, mainImageFile, additionalSlots) => {
    const formData = new FormData();
    if (mainImageFile) {
      formData.append('mainImage', {
        uri: mainImageFile.uri,
        type: mainImageFile.type || 'image/jpeg',
        name: mainImageFile.fileName || 'main.jpg',
      });
    }
    const keepUrls = [];
    if (additionalSlots) {
      additionalSlots.forEach(slot => {
        if (slot.file) {
          formData.append('additionalImages', {
            uri: slot.file.uri,
            type: slot.file.type || 'image/jpeg',
            name: slot.file.fileName || 'additional.jpg',
          });
        } else if (slot.uri) {
          keepUrls.push(slot.uri);
        }
      });
    }
    if (keepUrls.length > 0) {
      formData.append('keepAdditionalUrls', keepUrls.join(','));
    }
    const response = await inventoryApi.readymades.uploadImages(id, formData);
    const updated = response.data.data;
    set(state => ({
      readymades: state.readymades.map(a => (a.id === id ? updated : a)),
    }));
    return updated;
  },

  // Purchase Orders
  fetchPurchaseOrders: async (from, to, catalogItemType, orgUnitId) => {
    set({isLoading: true});
    try {
      const response = await inventoryApi.purchaseOrders.list(from, to, catalogItemType, orgUnitId);
      set({purchaseOrders: response.data.data});
    } finally {
      set({isLoading: false});
    }
  },
  createPurchaseOrder: async (data) => {
    const response = await inventoryApi.purchaseOrders.create(data);
    const newItem = response.data.data;
    set(state => ({purchaseOrders: [newItem, ...state.purchaseOrders]}));
    return newItem;
  },
  updatePurchaseOrder: async (id, data) => {
    const response = await inventoryApi.purchaseOrders.update(id, data);
    const updated = response.data.data;
    set(state => ({
      purchaseOrders: state.purchaseOrders.map(a => (a.id === id ? updated : a)),
    }));
    return updated;
  },
  deletePurchaseOrder: async (id) => {
    await inventoryApi.purchaseOrders.remove(id);
    set(state => ({
      purchaseOrders: state.purchaseOrders.filter(a => a.id !== id),
    }));
  },
  updatePurchaseOrderStatus: async (id, status) => {
    const response = await inventoryApi.purchaseOrders.updateStatus(id, status);
    const updated = response.data.data;
    set(state => ({
      purchaseOrders: state.purchaseOrders.map(a => (a.id === id ? updated : a)),
    }));
    return updated;
  },
}));
